import type { Drawing, DrawingPayload, IsoDate } from '@stockstop/shared';
import { daysBetween, DRAWING_Z_ORDER } from '@stockstop/shared';
import type {
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts';
import type { ChartCoordinates } from './coordinates.js';
import { rendererFor } from './registry.js';
import type { DragContext, HandleId, HitResult, Point, RenderContext } from './types.js';

/**
 * The drawing layer.
 *
 * Registered with lightweight-charts as a series primitive, which gives it a
 * canvas that sits above the candles and is repainted on every zoom, pan and
 * resize — the drawings therefore stay locked to their data coordinates for free.
 *
 * The layer owns everything common to all drawing types: which one is selected,
 * which is being dragged, hit-test ordering, and the draft geometry shown while a
 * drag is in flight. The individual types only answer questions about their own
 * shape.
 */

export interface DrawingLayerCallbacks {
  /** A drag finished; persist the new geometry. */
  onGeometryCommitted: (drawingId: number, payload: DrawingPayload) => void;
  /** Selection changed, so the surrounding UI can follow. */
  onSelectionChanged: (drawingId: number | null) => void;
  /** Die ausgewählte Zeichnung soll entfernt werden (Entf-Taste). */
  onDeleteRequested: (drawingId: number) => void;
  /** Pointer style the container should apply. */
  onCursorChanged: (cursor: string) => void;
}

interface DragState {
  drawingId: number;
  handle: HandleId;
  startDate: IsoDate;
  startPrice: number;
  /** Geometry when the drag began, so every move is relative to a fixed origin. */
  originalPayload: DrawingPayload;
}

export class DrawingLayer implements ISeriesPrimitive<Time> {
  private drawings: Drawing[] = [];
  private selectedId: number | null = null;
  /** Zeichnung unter dem Zeiger; steuert die Beschriftungen. */
  private hoveredId: number | null = null;

  /**
   * Zeichenkontext für die Treffererkennung.
   *
   * Die Renderer brauchen für `hitTest` einen `RenderContext`, rechnen darin aber
   * nur mit Geometrie — gezeichnet wird nichts. Ein einziger, einmal angelegter
   * Kontext genügt daher. Zuvor entstand bei **jeder** Mausbewegung ein neues
   * Canvas-Element; bei einem Zeiger, der über den Chart fährt, sind das hunderte
   * pro Sekunde.
   */
  private measuringContext: CanvasRenderingContext2D | null = null;

  private getMeasuringContext(): CanvasRenderingContext2D | null {
    this.measuringContext ??= document.createElement('canvas').getContext('2d');
    return this.measuringContext;
  }
  private drag: DragState | null = null;
  /** Geometry being previewed mid-drag, keyed by drawing id. */
  private drafts = new Map<number, DrawingPayload>();

  private requestUpdate: (() => void) | null = null;
  private latestDate: IsoDate | null = null;
  private latestClose: number | null = null;
  private currency: string | null = null;

  private readonly paneView: IPrimitivePaneView;

  constructor(
    private readonly coordinates: ChartCoordinates,
    private readonly callbacks: DrawingLayerCallbacks,
  ) {
    const renderer: IPrimitivePaneRenderer = {
      draw: (target) => {
        /*
         * Media coordinate space means drawing in CSS pixels while the library
         * handles device-pixel scaling, so lines stay crisp on retina displays
         * without any manual ratio arithmetic.
         */
        target.useMediaCoordinateSpace((scope) => {
          this.paint(scope.context, scope.mediaSize.width, scope.mediaSize.height);
        });
      },
    };

    this.paneView = {
      renderer: () => renderer,
      /* Above the candles, below the crosshair. */
      zOrder: () => 'top',
    };
  }

  /* ---------------------------------------------------------------- lifecycle */

  attached(param: SeriesAttachedParameter<Time>): void {
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.requestUpdate = null;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.paneView];
  }

  /** Asks the chart for a repaint; the chart decides when it actually happens. */
  private invalidate(): void {
    this.requestUpdate?.();
  }

  /* ------------------------------------------------------------------- inputs */

  setDrawings(drawings: Drawing[]): void {
    this.drawings = drawings;
    /* Verweise auf entfernte Zeichnungen aufräumen. */
    if (this.selectedId !== null && !drawings.some((d) => d.id === this.selectedId)) {
      this.selectedId = null;
      this.callbacks.onSelectionChanged(null);
    }
    if (this.hoveredId !== null && !drawings.some((d) => d.id === this.hoveredId)) {
      this.hoveredId = null;
    }
    /* Drop drafts for drawings that no longer exist. */
    for (const id of [...this.drafts.keys()]) {
      if (!drawings.some((drawing) => drawing.id === id)) this.drafts.delete(id);
    }
    this.invalidate();
  }

  setMarketContext(
    latestDate: IsoDate | null,
    latestClose: number | null,
    currency: string | null,
  ): void {
    this.latestDate = latestDate;
    this.latestClose = latestClose;
    this.currency = currency;
    this.invalidate();
  }

  setSelected(drawingId: number | null): void {
    if (this.selectedId === drawingId) return;
    this.selectedId = drawingId;
    this.callbacks.onSelectionChanged(drawingId);
    this.invalidate();
  }

  get selection(): number | null {
    return this.selectedId;
  }

  get isDragging(): boolean {
    return this.drag !== null;
  }

  /* ---------------------------------------------------------------- rendering */

  private renderContext(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    drawingId: number,
  ): RenderContext {
    return {
      ctx,
      coordinates: this.coordinates,
      width,
      height,
      latestDate: this.latestDate,
      latestClose: this.latestClose,
      currency: this.currency,
      isSelected: drawingId === this.selectedId,
      isHovered: drawingId === this.hoveredId,
    };
  }

  /** Meldet, worüber der Zeiger steht; löst bei Änderung ein Neuzeichnen aus. */
  setHovered(drawingId: number | null): void {
    if (this.hoveredId === drawingId) return;
    this.hoveredId = drawingId;
    this.invalidate();
  }

  /** Fordert das Entfernen der ausgewählten Zeichnung an (Entf-Taste). */
  requestDeleteSelected(): boolean {
    if (this.selectedId === null) return false;
    this.callbacks.onDeleteRequested(this.selectedId);
    return true;
  }

  /** Geometry to render: the draft while dragging, otherwise the stored payload. */
  private effectivePayload(drawing: Drawing): DrawingPayload {
    return this.drafts.get(drawing.id) ?? drawing.payload;
  }

  /**
   * Zeichnungen von hinten nach vorne.
   *
   * `sort` ist stabil, gleichrangige Typen behalten daher ihre bisherige
   * Reihenfolge — bei gleicher Ebene liegt weiterhin die jüngere Zeichnung oben.
   */
  private inRenderOrder(): Drawing[] {
    return [...this.drawings].sort(
      (a, b) => DRAWING_Z_ORDER[a.payload.type] - DRAWING_Z_ORDER[b.payload.type],
    );
  }

  private paint(ctx: CanvasRenderingContext2D, width: number, height: number): void {
    for (const drawing of this.inRenderOrder()) {
      const payload = this.effectivePayload(drawing);
      const renderer = rendererFor(payload);
      const context = this.renderContext(ctx, width, height, drawing.id);

      ctx.save();
      try {
        renderer.draw(payload, drawing.style, drawing.label, drawing.showPrice, context);
      } finally {
        /* One failing drawing must not abort the frame for the others. */
        ctx.restore();
      }
    }
  }

  /* -------------------------------------------------------------- interaction */

  /**
   * Finds the drawing under the pointer.
   *
   * Named `findAt` rather than `hitTest`, because `ISeriesPrimitive` reserves
   * `hitTest` for its own `(x, y)` signature and implementing it here would
   * override the library's contract with an incompatible one.
   *
   * Iterates back to front so the most recently created drawing — the one drawn on
   * top — is also the one the user grabs.
   */
  findAt(point: Point, width: number, height: number): HitResult | null {
    const ctx = this.getMeasuringContext();
    if (!ctx) return null;

    /* Rückwärts durch dieselbe Reihenfolge: Was oben liegt, wird zuerst geprüft. */
    const ordered = this.inRenderOrder();
    for (let i = ordered.length - 1; i >= 0; i -= 1) {
      const drawing = ordered[i]!;
      const payload = this.effectivePayload(drawing);
      const renderer = rendererFor(payload);
      const context = this.renderContext(ctx, width, height, drawing.id);
      const handle = renderer.hitTest(payload, point, context);

      if (handle) {
        return {
          drawingId: drawing.id,
          handle,
          cursor: handle === 'body' ? 'move' : handle === 'price' ? 'ns-resize' : 'pointer',
        };
      }
    }
    return null;
  }

  /** Begins a drag. Returns true when a drawing was grabbed. */
  beginDrag(hit: HitResult, point: Point): boolean {
    const drawing = this.drawings.find((entry) => entry.id === hit.drawingId);
    if (!drawing) return false;

    const date = this.coordinates.dateForX(point.x);
    const price = this.coordinates.priceForY(point.y);
    if (date === null || price === null) return false;

    this.drag = {
      drawingId: drawing.id,
      handle: hit.handle,
      startDate: date,
      startPrice: price,
      originalPayload: this.effectivePayload(drawing),
    };
    this.setSelected(drawing.id);
    return true;
  }

  /** Updates the draft geometry while the pointer moves. */
  updateDrag(point: Point): void {
    if (!this.drag) return;

    const date = this.coordinates.dateForX(point.x);
    const price = this.coordinates.priceForY(point.y);
    if (date === null || price === null) return;

    const drag: DragContext = {
      point,
      date,
      price,
      deltaPrice: price - this.drag.startPrice,
      deltaDays: daysBetween(this.drag.startDate, date),
      startDate: this.drag.startDate,
      startPrice: this.drag.startPrice,
    };

    const renderer = rendererFor(this.drag.originalPayload);
    const next = renderer.applyDrag(this.drag.originalPayload, this.drag.handle, drag);
    if (next) {
      this.drafts.set(this.drag.drawingId, next);
      this.invalidate();
    }
  }

  /** Ends the drag and hands the final geometry to the caller for persisting. */
  endDrag(): void {
    if (!this.drag) return;

    const { drawingId } = this.drag;
    const draft = this.drafts.get(drawingId);
    this.drag = null;

    if (draft) {
      /*
       * The draft stays in place until the server echoes the saved drawing back.
       * Clearing it here would snap the shape to its old position for one frame.
       */
      this.callbacks.onGeometryCommitted(drawingId, draft);
    }
  }

  /** Drops a draft once the saved version has arrived. */
  confirmSaved(drawingId: number): void {
    this.drafts.delete(drawingId);
    this.invalidate();
  }

  cancelDrag(): void {
    if (!this.drag) return;
    this.drafts.delete(this.drag.drawingId);
    this.drag = null;
    this.invalidate();
  }
}
